/**
 * Felix Vimeo — <felix-vimeo>
 *
 * Click-to-play facade. The server renders a poster inside a real link to the video
 * on vimeo.com, so it still works without JS; on click the link is swapped for a
 * Vimeo player iframe that autoplays. Nothing is requested from Vimeo before the
 * click apart from a preconnect once the visitor shows intent.
 *
 * Vimeo, not YouTube, because Vimeo never runs ads, so a competitor's preroll can't play here.
 *
 * @package Felix
 */

const EMBED_ORIGIN = 'https://player.vimeo.com'
const PRECONNECT_ORIGINS = [EMBED_ORIGIN, 'https://i.vimeocdn.com', 'https://f.vimeocdn.com']

let preconnected = false

function preconnect() {
	if (preconnected) return
	preconnected = true
	PRECONNECT_ORIGINS.forEach((href) => {
		const link = document.createElement('link')
		link.rel = 'preconnect'
		link.href = href
		document.head.append(link)
	})
}

class FelixVimeo extends HTMLElement {
	connectedCallback() {
		this.trigger = this.querySelector('a')
		const videoId = this.dataset.videoId
		if (!this.trigger || !/^\d+$/.test(videoId || '')) return

		this.trigger.addEventListener('pointerenter', preconnect, { once: true })
		this.trigger.addEventListener('focus', preconnect, { once: true })
		this.trigger.addEventListener('click', (event) => {
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return
			event.preventDefault()
			this.play(videoId)
		})
	}

	play(videoId) {
		const params = new URLSearchParams({ autoplay: '1', dnt: '1', playsinline: '1' })
		const hash = this.dataset.hash || ''
		if (/^[A-Za-z0-9]+$/.test(hash)) params.set('h', hash)

		const iframe = document.createElement('iframe')
		iframe.src = `${EMBED_ORIGIN}/video/${videoId}?${params}`
		iframe.title = this.dataset.title || 'Vimeo video'
		iframe.allow = 'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
		iframe.allowFullscreen = true
		iframe.referrerPolicy = 'strict-origin-when-cross-origin'
		iframe.className = 'absolute inset-0 w-full h-full'

		this.trigger.replaceWith(iframe)
		iframe.focus()
	}
}

if (!customElements.get('felix-vimeo')) {
	customElements.define('felix-vimeo', FelixVimeo)
}
